import { RuleSetType } from './ActivityGroup'
import store from 'state/store'
import { Action, addListener } from '@reduxjs/toolkit'
import { interactionModelActions } from 'modules/interaction/redux/slice'
import { useCallback, useEffect } from 'react'
import {
  Activity,
  ActivityValue,
  InteractionModel,
  NestableGroupValue
} from 'types/shared'
import { flatValuesToOldActivities } from 'modules/interaction/utils/flatValuesToOldActivities'
import { useGroupValidationContext } from './GroupValidationContext'
import { v4 as uuidv4 } from 'uuid'

type useParentDependencyProps = {
  ruleSet?: RuleSetType
  path: string[]
  interactionModel: InteractionModel
  onChange: (activities: string) => void
  values: NestableGroupValue
  uuid: string
}

const useParentDependency = ({
  ruleSet,
  path,
  interactionModel,
  onChange,
  values,
  uuid
}: useParentDependencyProps) => {
  const { subscribeDependency, unsubscribeDependency, hasSubscription } =
    useGroupValidationContext()

  const emptyGroup = useCallback(
    (
      groupValues: Record<string, ActivityValue | NestableGroupValue>
    ): Record<string, ActivityValue | NestableGroupValue> => {
      const result: Record<string, ActivityValue | NestableGroupValue> = {}

      Object.entries(groupValues).forEach(([activityUuid, value]) => {
        const activityModelInput =
          interactionModel.flatData?.activity_models[activityUuid]

        if (!activityModelInput) {
          return
        }

        if (activityModelInput.content_type === 'activity_group') {
          if (Array.isArray(value)) {
            result[activityUuid] = value.map((group) => ({
              ...group,
              values: emptyGroup(group.values)
            }))
          } else {
            result[activityUuid] = [
              {
                uuid: uuidv4(),
                values: {}
              }
            ]
          }
        }
      })

      return result
    },
    [interactionModel.flatData?.activity_models]
  )

  const resetGroupValues = useCallback(() => {
    const resetValues = values.map((group) => ({
      ...group,
      values: emptyGroup(group.values)
    }))

    const groupValues = {
      [uuid]: resetValues
    }

    onChange(flatValuesToOldActivities(groupValues)[0].des)

    return [
      path.slice(0, -1),
      flatValuesToOldActivities(groupValues)[0].des
    ] as [string[], string]
  }, [values, path, uuid, onChange, emptyGroup])

  const getCurrentGroup = useCallback(
    (
      activity: Activity,
      path: string[],
      pathIndex: number
    ): Activity | null => {
      if (activity.uuid_activity_model === path[path.length - 1]) {
        return activity
      }

      const [activityModelUuid, groupIndex] = path.slice(
        pathIndex,
        pathIndex + 2
      )
      if (activity.uuid_activity_model === activityModelUuid) {
        const groups: Activity[][] = activity.des
          ? JSON.parse(activity.des)
          : []

        for (const group of groups[Number(groupIndex)] || []) {
          const result = getCurrentGroup(group, path, pathIndex + 2)
          if (result !== null) {
            return result
          }
        }
      }

      return null
    },
    []
  )

  const isActivityEmpty = useCallback(
    (activity: Activity): boolean => {
      if (
        interactionModel.flatData?.activity_models[activity.uuid_activity_model]
          .content_type === 'activity_group'
      ) {
        const groups: Activity[][] = activity.des
          ? JSON.parse(activity.des)
          : []
        return groups.every((group) => group.every(isActivityEmpty))
      }

      return false
    },
    [interactionModel.flatData?.activity_models]
  )

  useEffect(() => {
    if (!ruleSet?.depends_on) return

    const unsubscribe = store.dispatch(
      addListener({
        predicate: (
          action: Action
        ): action is ReturnType<
          typeof interactionModelActions.updateActivities
        > => interactionModelActions.updateActivities.match(action),
        effect: (action) => {
          const parentActivity = action.payload.activities.filter(
            (activity) => activity.uuid_activity_model === path[0]
          )[0]

          if (!parentActivity) {
            return
          }

          const currentGroup = getCurrentGroup(
            parentActivity,
            path.slice(0, -1),
            0
          )
          const groups: Activity[][] = currentGroup?.des
            ? JSON.parse(currentGroup.des)
            : []

          const isGroupEmpty = groups.every((group) =>
            group.every(isActivityEmpty)
          )

          if (isGroupEmpty) {
            unsubscribeDependency(ruleSet.depends_on, uuid)

            return
          }

          if (!hasSubscription(uuid)) {
            subscribeDependency(ruleSet.depends_on, uuid, resetGroupValues)
          }
        }
      })
    )

    return unsubscribe
  }, [
    getCurrentGroup,
    hasSubscription,
    isActivityEmpty,
    path,
    resetGroupValues,
    ruleSet?.depends_on,
    subscribeDependency,
    unsubscribeDependency,
    uuid
  ])
}

export default useParentDependency
